export const CART_LAYOUT_ROW_TYPES = Object.freeze({
  PRODUCT: "product",
  SECTION: "section",
});

export const normalizeSectionTitle = (value = "") => String(value || "").trim();
export const normalizeSectionStyle = (value) =>
  String(value || "").trim().toLowerCase() === "dark" ? "dark" : "light";

const resolvePersistedSortOrder = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const resolveSortOrder = (row = {}, fallbackIndex = 0) => {
  const numeric = resolvePersistedSortOrder(row?.sortOrder ?? row?.SortOrder);
  return numeric !== null ? numeric : fallbackIndex;
};

const resolveTimestamp = (row = {}) => {
  const timestamp = new Date(row?.createdAt ?? row?.CreatedAt ?? row?.updatedAt ?? row?.UpdatedAt ?? 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

const compareCartLayoutRows = (left, right) => {
  const leftSortValue = Object.prototype.hasOwnProperty.call(left || {}, 'rawSortOrder')
    ? left.rawSortOrder
    : left?.sortOrder;
  const rightSortValue = Object.prototype.hasOwnProperty.call(right || {}, 'rawSortOrder')
    ? right.rawSortOrder
    : right?.sortOrder;
  const leftSortOrder = resolvePersistedSortOrder(leftSortValue);
  const rightSortOrder = resolvePersistedSortOrder(rightSortValue);

  if (leftSortOrder !== null && rightSortOrder !== null && leftSortOrder !== rightSortOrder) {
    return leftSortOrder - rightSortOrder;
  }

  if ((leftSortOrder !== null) !== (rightSortOrder !== null)) {
    return leftSortOrder !== null ? -1 : 1;
  }

  const timestampDelta = resolveTimestamp(left) - resolveTimestamp(right);
  if (timestampDelta !== 0) return timestampDelta;

  const leftId = Number(left?.orderItemId ?? left?.sectionId ?? left?.id ?? 0);
  const rightId = Number(right?.orderItemId ?? right?.sectionId ?? right?.id ?? 0);
  if (leftId !== rightId) return leftId - rightId;

  if (left?.type !== right?.type) {
    return left?.type === CART_LAYOUT_ROW_TYPES.PRODUCT ? -1 : 1;
  }

  return String(left.layoutId).localeCompare(String(right.layoutId));
};

export const buildCartLayout = ({ products = [], sections = [] } = {}) => {
  const productRows = (Array.isArray(products) ? products : []).map((product, index) => ({
    ...product,
    type: CART_LAYOUT_ROW_TYPES.PRODUCT,
    layoutId: `product:${product?.cartLineId ?? product?.orderItemId ?? product?.OrderItemId ?? product?.id ?? index}`,
    orderItemId: product?.orderItemId ?? product?.OrderItemId ?? product?.id ?? null,
    cartLineId: product?.cartLineId ?? null,
    rawSortOrder: product?.sortOrder ?? product?.SortOrder ?? null,
    sortOrder: resolveSortOrder(product, index),
    createdAt: product?.createdAt ?? product?.CreatedAt ?? null,
  }));

  const sectionRows = (Array.isArray(sections) ? sections : []).map((section, index) => ({
    ...section,
    type: CART_LAYOUT_ROW_TYPES.SECTION,
    layoutId: `section:${section?.id ?? section?.sectionId ?? index}`,
    sectionId: section?.sectionId ?? section?.id ?? null,
    title: normalizeSectionTitle(section?.title),
    style: normalizeSectionStyle(section?.style ?? section?.Style),
    rawSortOrder: section?.sortOrder ?? section?.SortOrder ?? null,
    sortOrder: resolveSortOrder(section, products.length + index),
    createdAt: section?.createdAt ?? section?.CreatedAt ?? null,
  }));

  return [...productRows, ...sectionRows].sort(compareCartLayoutRows).map(({ rawSortOrder, ...row }) => row);
};

// SelectedPresentation rows use itemId as their UI identity.  Keep that identity
// explicit when adapting them to cart layout rows; orderItemId is persistence
// lineage and is not unique for legacy/copied lines.
export const buildProductLayoutRows = (products = []) =>
  (Array.isArray(products) ? products : []).map((row, index) => {
    const cartLineId = row?.cartLineId ?? row?.itemId ?? null;
    return {
      ...row,
      type: CART_LAYOUT_ROW_TYPES.PRODUCT,
      cartLineId,
      layoutId: `product:${cartLineId ?? row?.orderItemId ?? row?.dbItemId ?? index}`,
      sortOrder: row?.sortOrder ?? row?.SortOrder ?? null,
    };
  });
